import { Map } from 'immutable'
import { diffWordsWithSpace } from 'diff'
import _ from 'lodash'

import { TextReviewInteractivityAction } from '../../constants/ACTION_FLOW_KEYS'
import { isParentRepeat, getParentRepeatUID } from '../../store/project/processCondCache'

import {
  handleAcceptAllChanges,
  handleRevertAllChanges,
  getTextContentFromTokens,
  minifyTextTokens,
  unminifyTextTokens,
  getRemainingChangesCount,
  getSingleElementStateByIndex,
} from '../textReviewWidget'

export const handleTextReviewInteractivity = (actionTarget, state, index) => {
  const settings = actionTarget.getIn([0], actionTarget.getIn(['0'], Map()))
  const action = settings.get('action', '')

  const { path: elementStatePath, hasRepeatParent } = getElementStateInfo(settings, state)
  const elementState = state.getIn(elementStatePath, Map()).toJS()

  const updatedElementState = hasRepeatParent
    ? handleForRepeatedElement(action, elementState, index)
    : handleForStandaloneElement(action, elementState)

  return {
    path: elementStatePath,
    state: updatedElementState,
  }
}

const handleForStandaloneElement = (action, state) => {
  const { draft, value, changesNumber } = handleAction(action, state)

  return {
    old: state.old,
    new: state.new,
    value,
    draft,
    changesNumber,
  }
}

const handleForRepeatedElement = (action, state, elementIndex) => {
  const updatedDraft = _.clone(state.draft)
  const updatedValue = _.clone(state.value)
  const updatedChangesNumber = _.clone(state.changesNumber)

  state?.old?.forEach((__, index) => {
    if (_.isNumber(elementIndex) && index !== elementIndex) {
      return
    }

    const stringIndex = String(index)

    const currentElementState = getSingleElementStateByIndex(state, stringIndex)
    const { draft, value, changesNumber } = handleAction(action, currentElementState)

    updatedDraft[stringIndex] = draft
    updatedValue[stringIndex] = value
    updatedChangesNumber[stringIndex] = changesNumber
  })

  return {
    old: state.old,
    new: state.new,
    value: updatedValue,
    draft: updatedDraft,
    changesNumber: updatedChangesNumber,
  }
}

const handleAction = (action, state) => {
  let textTokens = []

  try {
    textTokens = unminifyTextTokens(JSON.parse(state.draft))
  } catch {}

  const payload = {
    textTokens,
    value: state.value,
    changesNumber: state.changesNumber,
    old: state.old,
    new: state.new,
  }

  let result

  switch (action) {
    case TextReviewInteractivityAction.ACCEPT_ALL: {
      result = handleAcceptAllAction(payload)
      break
    }

    case TextReviewInteractivityAction.REVERT_ALL: {
      result = handleRevertAllAction(payload)
      break
    }

    case TextReviewInteractivityAction.RESET_CHANGES: {
      result = handleResetChangesAction(payload)
      break
    }

    default: {
      result = _.cloneDeep(payload)
    }
  }

  return {
    value: result.value,
    changesNumber: result.changesNumber,
    draft: JSON.stringify(minifyTextTokens(result.textTokens)),
  }
}

const handleAcceptAllAction = (payload) => {
  let { textTokens, value } = payload

  if (payload.changesNumber) {
    textTokens = handleAcceptAllChanges(payload.textTokens)
    value = getTextContentFromTokens(textTokens)
  }

  return {
    textTokens,
    value,
    changesNumber: 0,
  }
}

const handleRevertAllAction = (payload) => {
  let { textTokens, value } = payload

  if (payload.changesNumber) {
    textTokens = handleRevertAllChanges(payload.textTokens)
    value = getTextContentFromTokens(textTokens)
  }

  return {
    textTokens,
    value,
    changesNumber: 0,
  }
}

const handleResetChangesAction = (payload) => {
  const textsAreIdentical = payload.old === payload.new

  const textTokens = textsAreIdentical ? [{ value: payload.old }] : diffWordsWithSpace(payload.old, payload.new)
  const value = textsAreIdentical ? payload.old : ''
  const changesNumber = getRemainingChangesCount(textTokens)

  return {
    textTokens,
    changesNumber,
    value,
  }
}

const getElementStateInfo = (settings, state) => {
  let elementPath = settings.get('element', '').split(':')

  const elementUID = elementPath[elementPath.length - 1]
  const hasRepeatParent = isParentRepeat(state, elementUID)

  if (hasRepeatParent && elementPath.length < 3) {
    // has repeat parent but it is not included in element path
    const parentRepeatUID = getParentRepeatUID(state, elementUID)
    const pageUID = elementPath[0]

    elementPath = [pageUID, parentRepeatUID, elementUID]
  }

  return {
    hasRepeatParent,
    path: ['state', ...elementPath],
  }
}
